// @ts-nocheck
import { TAU, clamp, rand } from "../../util";
import { Particle } from "../../entities";
import { Game } from "../game-base";

/**
 * Per-boss idle ambience. Each boss has a distinct *signature* style (not just a
 * recolored glow): arcane sigils, rising flame, ground quakes, plague bubbles,
 * drifting frost, radiant god-rays, writhing void tendrils, holy light shafts, or an
 * inward void pull.
 *
 * Drawn in two passes from drawPlayer (origin at the boss's feet anchor, additive
 * blending): "back" runs behind the sprite (ground decals, rear motes, auras),
 * "front" runs over the sprite (the lively bits that should float in front).
 *
 *   style  : which signature routine to run
 *   glow   : primary themed color  [r,g,b]
 *   accent : brighter highlight    [r,g,b]
 */
export const BOSS_AMBIENT = {
  kael:      { style: "arcane",  glow: [168, 104, 232], accent: [210, 160, 255] },
  angel:     { style: "fire",    glow: [255, 130, 56],  accent: [255, 206, 120] },
  titan:     { style: "quake",   glow: [150, 100, 220], accent: [188, 130, 255] },
  groshkar:  { style: "plague",  glow: [150, 190, 70],  accent: [196, 226, 96] },
  vorathal:  { style: "frost",   glow: [150, 200, 255], accent: [214, 238, 255] },
  aurelion:  { style: "radiant", glow: [255, 210, 120], accent: [255, 242, 186] },
  ixtholoth: { style: "void",    glow: [150, 80, 226],  accent: [202, 132, 255] },
  morwenna:  { style: "holy",    glow: [255, 214, 120], accent: [255, 246, 200] },
  lys:       { style: "umbral",  glow: [110, 140, 255], accent: [176, 158, 255] },
};

// Drifting/rising particle spec per style (uses the shared particle budget).
const STYLE_PARTICLES = {
  fire:    { chance: 0.30, src: "accent", vy: [-95, -48], vx: 18, life: 0.6, size: [1.4, 3.0], grav: -20 },
  plague:  { chance: 0.10, src: "accent", vy: [-22, -6],  vx: 16, life: 0.9, size: [1.6, 3.2], grav: -6 },
  frost:   { chance: 0.22, src: "accent", vy: [22, 46],   vx: 12, life: 1.0, size: [1.2, 2.6], grav: 10, fromTop: true },
  holy:    { chance: 0.12, src: "accent", vy: [-34, -14], vx: 10, life: 1.0, size: [1.2, 2.2], grav: -8 },
  umbral:  { chance: 0.16, src: "accent", vy: [16, 34],   vx: 10, life: 1.0, size: [1.4, 2.6], grav: 8,  fromTop: true },
  quake:   { chance: 0.14, src: "glow",   vy: [10, 26],   vx: 8,  life: 0.7, size: [1.2, 2.4], grav: 22, fromTop: true },
  arcane:  { chance: 0.06, src: "accent", vy: [-42, -18], vx: 14, life: 0.6, size: [1.4, 2.4] },
  radiant: { chance: 0.05, src: "accent", vy: [-32, -12], vx: 12, life: 0.6, size: [1.4, 2.4] },
  void:    { chance: 0.05, src: "accent", vy: [-30, -12], vx: 12, life: 0.6, size: [1.4, 2.4] },
};

// Torso height above the feet anchor. Billboard bosses tower upward from the
// feet; the canvas bodies (aurelion, ixtholoth) sit near the origin.
function bossAmbientCenterY(bossId, radius) {
  if (bossId === "aurelion" || bossId === "ixtholoth") return -radius * 0.2;
  return -radius * 3.6;
}

// Global intensity multiplier so the whole system can be dialed up/down at once.
const AMP = 2.0;

// Gradients are expensive to build; cache one full-opacity blob per (color,
// radius-bucket) at the origin and reuse it by translating + scaling alpha.
const blobGradCache = new Map();
function blobGradient(c, r, col) {
  const rr = Math.max(1, Math.round(r / 4) * 4);
  const key = `${col[0]},${col[1]},${col[2]}:${rr}`;
  let g = blobGradCache.get(key);
  if (!g) {
    g = c.createRadialGradient(0, 0, 0, 0, 0, rr);
    g.addColorStop(0, `rgba(${col[0]},${col[1]},${col[2]},1)`);
    g.addColorStop(1, `rgba(${col[0]},${col[1]},${col[2]},0)`);
    blobGradCache.set(key, g);
  }
  return { g, rr };
}

// Soft additive radial blob.
function blob(c, x, y, r, col, a) {
  a *= AMP;
  if (a <= 0 || r <= 0) return;
  const { g, rr } = blobGradient(c, r, col);
  c.save();
  c.globalAlpha *= a < 1 ? a : 1;
  c.translate(x, y);
  c.fillStyle = g;
  c.beginPath();
  c.arc(0, 0, rr, 0, TAU);
  c.fill();
  c.restore();
}

function rgbaStr(col, a) { return `rgba(${col[0]},${col[1]},${col[2]},${a * AMP})`; }

// Kael — rotating arcane hexagram sigil on the floor + flickering glyph motes
// orbiting the torso (rear motes behind, front motes over the body).
function fxArcane(c, r, tt, cy, tilt, col, acc, front) {
  if (!front) {
    c.save();
    c.scale(1, tilt);
    c.rotate(tt * 0.2);
    const R = r * 3.3;
    c.strokeStyle = rgbaStr(col, 0.3 + 0.1 * Math.sin(tt * 2));
    c.lineWidth = 2.6;
    for (let t = 0; t < 2; t++) {
      c.beginPath();
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * TAU - Math.PI / 2 + t * Math.PI;
        const x = Math.cos(a) * R, y = Math.sin(a) * R;
        i ? c.lineTo(x, y) : c.moveTo(x, y);
      }
      c.closePath();
      c.stroke();
    }
    // counter-rotating inner + outer rings, with radial glyph ticks
    c.beginPath(); c.arc(0, 0, R * 0.62, 0, TAU); c.stroke();
    c.lineWidth = 1.6;
    c.beginPath(); c.arc(0, 0, R * 1.04, 0, TAU); c.stroke();
    const ticks = 12;
    for (let i = 0; i < ticks; i++) {
      const a = (i / ticks) * TAU - tt * 0.4;
      c.beginPath();
      c.moveTo(Math.cos(a) * R * 0.9, Math.sin(a) * R * 0.9);
      c.lineTo(Math.cos(a) * R * 1.04, Math.sin(a) * R * 1.04);
      c.stroke();
    }
    c.restore();
    blob(c, 0, cy, r * 3.0, col, 0.14);
  }
  const orx = r * 3.0, ory = r * 1.3, n = 6;
  for (let i = 0; i < n; i++) {
    const a = tt * 0.7 + (i / n) * TAU;
    const sin = Math.sin(a);
    if ((sin > 0) !== front) continue;
    const x = Math.cos(a) * orx, y = cy + sin * ory;
    const depth = 0.55 + 0.45 * ((sin + 1) / 2);
    const flick = 0.5 + 0.5 * Math.sin(tt * 5 + i * 1.7);
    blob(c, x, y, r * 0.28 * depth, acc, 0.85 * depth * flick);
  }
}

// Inferna — flickering flame light, an ember pool, and licking flames at the base.
function fxFire(c, r, tt, cy, tilt, col, acc, front) {
  const flick = 0.6 + 0.4 * ((Math.sin(tt * 9) + Math.sin(tt * 13.7) + Math.sin(tt * 23.3)) / 3);
  if (!front) {
    c.save();
    c.scale(1, tilt);
    blob(c, 0, 0, r * 2.9, col, 0.15 * flick);
    c.restore();
    blob(c, 0, cy, r * 3.2, col, 0.16 * flick);
    return;
  }
  const n = 6;
  for (let i = 0; i < n; i++) {
    const fx = (i / (n - 1) - 0.5) * r * 2.2;
    const h = r * (0.9 + 0.8 * Math.abs(Math.sin(tt * 6 + i * 1.5)));
    blob(c, fx, cy * 0.2 - h * 0.5, r * 0.35, acc, 0.24 * flick);
  }
  blob(c, 0, cy + r * 0.6, r * 1.7, acc, 0.14 * flick);
}

// Titan — glowing fissure cracks radiating across the floor + a heavy pulse.
function fxQuake(c, r, tt, cy, tilt, col, acc, front) {
  if (front) return;
  c.save();
  c.scale(1, tilt);
  const R = r * 3.6, n = 8;
  const pulse = 0.18 + 0.16 * Math.abs(Math.sin(tt * 1.4));
  c.strokeStyle = rgbaStr(acc, pulse);
  c.lineWidth = 3.2;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + (i % 2 ? 0.25 : -0.15);
    c.beginPath();
    c.moveTo(0, 0);
    for (let s = 1; s <= 3; s++) {
      const rr = (R * s) / 3;
      const aa = a + (s % 2 ? 0.18 : -0.18);
      c.lineTo(Math.cos(aa) * rr, Math.sin(aa) * rr);
    }
    c.stroke();
  }
  blob(c, 0, 0, r * 2.2, col, 0.13);
  c.restore();
  blob(c, 0, cy, r * 2.8, col, 0.09);
}

// Grosh'kar — rising spore bubbles that swell and pop + a low miasma haze + floor spore fog.
function fxPlague(c, r, tt, cy, tilt, col, acc, front) {
  if (!front) {
    c.save();
    c.scale(1, tilt);
    for (let i = 0; i < 4; i++) {
      const a = tt * 0.25 + i * 1.57;
      blob(c, Math.cos(a) * r * 1.4, Math.sin(a) * r * 1.0, r * 1.9, col, 0.08 + 0.04 * Math.sin(tt * 2 + i));
    }
    blob(c, 0, 0, r * 3.2, col, 0.10);
    c.restore();
    blob(c, 0, cy, r * 2.8, col, 0.11);
    c.save();
    c.scale(1, tilt);
    c.globalAlpha = 0.18 + 0.08 * Math.sin(tt * 1.5);
    const fog = c.createRadialGradient(0, 0, r * 0.5, 0, 0, r * 3.5);
    fog.addColorStop(0, rgbaStr(acc, 0.35));
    fog.addColorStop(1, rgbaStr(col, 0));
    c.fillStyle = fog;
    c.beginPath();
    c.arc(0, 0, r * 3.5, 0, TAU);
    c.fill();
    c.restore();
  }
  const n = 6, H = r * 5;
  for (let i = 0; i < n; i++) {
    const ph = ((tt * 0.32) + i / n) % 1;
    if ((ph >= 0.5) !== front) continue;
    const y = cy + r * 1.5 - ph * H;
    const x = Math.sin(tt * 1.2 + i * 2.0) * r * 1.3 * (0.4 + ph);
    const rr = r * (0.12 + 0.26 * ph);
    const a = ph < 0.8 ? 0.4 : 0.4 * (1 - (ph - 0.8) / 0.2);
    blob(c, x, y, rr, acc, a * 0.5);
    c.strokeStyle = rgbaStr(acc, a);
    c.lineWidth = 2.2;
    c.beginPath();
    c.arc(x, y, rr, 0, TAU);
    c.stroke();
  }
}

// Vorathal — slow hovering frost shards + a cold ground mist (snow via particles).
function fxFrost(c, r, tt, cy, tilt, col, acc, front) {
  if (!front) {
    c.save();
    c.scale(1, tilt);
    blob(c, 0, 0, r * 3.0, col, 0.12);
    for (let i = 0; i < 2; i++) {
      blob(c, Math.cos(tt * 0.2 + i * Math.PI) * r * 1.4, 0, r * 1.6, acc, 0.06);
    }
    c.restore();
    blob(c, 0, cy, r * 2.6, col, 0.11);
  }
  const n = 4;
  for (let i = 0; i < n; i++) {
    const a = tt * 0.4 + (i / n) * TAU;
    const sin = Math.sin(a);
    if ((sin > 0) !== front) continue;
    const x = Math.cos(a) * r * 2.6;
    const y = cy + sin * r * 1.1 + Math.sin(tt * 1.5 + i) * r * 0.3;
    const depth = 0.55 + 0.45 * ((sin + 1) / 2);
    const sz = r * 0.36 * depth;
    blob(c, x, y, sz * 1.5, acc, 0.4 * depth);
    c.save();
    c.translate(x, y);
    c.rotate(Math.PI / 4 + tt * 0.5);
    c.fillStyle = rgbaStr(acc, 0.65 * depth);
    c.fillRect(-sz * 0.45, -sz * 0.45, sz * 0.9, sz * 0.9);
    c.restore();
  }
}

// Aurelion — rotating radiant god-rays + motes spiralling inward to the core.
function fxRadiant(c, r, tt, cy, tilt, col, acc, front) {
  const R = r * 3.9;
  if (!front) {
    c.save();
    c.translate(0, cy);
    c.rotate(tt * 0.25);
    const n = 10;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU;
      const w = 0.13;
      const fl = 0.16 + 0.09 * Math.sin(tt * 2 + i);
      const grad = c.createRadialGradient(0, 0, r * 0.3, 0, 0, R);
      grad.addColorStop(0, rgbaStr(acc, fl));
      grad.addColorStop(1, rgbaStr(acc, 0));
      c.fillStyle = grad;
      c.beginPath();
      c.moveTo(0, 0);
      c.lineTo(Math.cos(a - w) * R, Math.sin(a - w) * R);
      c.lineTo(Math.cos(a + w) * R, Math.sin(a + w) * R);
      c.closePath();
      c.fill();
    }
    c.restore();
    blob(c, 0, cy, r * 1.6, col, 0.12);
  }
  const n2 = 8;
  for (let i = 0; i < n2; i++) {
    const ph = ((tt * 0.5) + i / n2) % 1;
    const ang = i * 2.39 + ph * 5;
    if ((Math.sin(ang) > 0) !== front) continue;
    const rad = R * (1 - ph);
    const x = Math.cos(ang) * rad, y = cy + Math.sin(ang) * rad * 0.5;
    const a = ph < 0.85 ? 0.55 * ph : 0.55 * (1 - (ph - 0.85) / 0.15);
    blob(c, x, y, r * 0.16, acc, a);
  }
}

// Ix'tholoth — writhing void tendrils reaching out + motes pulled inward.
function fxVoid(c, r, tt, cy, tilt, col, acc, front) {
  const R = r * 3.5;
  if (!front) {
    blob(c, 0, cy, r * 2.8, col, 0.13);
    c.save();
    c.translate(0, cy);
    const n = 6, seg = 8;
    for (let i = 0; i < n; i++) {
      const base = (i / n) * TAU + tt * 0.25;
      c.beginPath();
      for (let s = 0; s <= seg; s++) {
        const t = s / seg;
        const rad = t * R;
        const ang = base + Math.sin(t * 3.5 + tt * 1.8 + i) * 0.5;
        const x = Math.cos(ang) * rad, y = Math.sin(ang) * rad * 0.55;
        s ? c.lineTo(x, y) : c.moveTo(x, y);
      }
      c.strokeStyle = rgbaStr(acc, 0.26);
      c.lineWidth = 2.8;
      c.stroke();
    }
    c.restore();
  }
  const n2 = 7;
  for (let i = 0; i < n2; i++) {
    const ph = ((tt * 0.45) + i / n2) % 1;
    const ang = i * 2.4 - ph * 5;
    if ((Math.sin(ang) > 0) !== front) continue;
    const rad = R * (1 - ph);
    const x = Math.cos(ang) * rad, y = cy + Math.sin(ang) * rad * 0.5;
    const a = ph < 0.85 ? 0.5 * ph : 0.5 * (1 - (ph - 0.85) / 0.15);
    blob(c, x, y, r * 0.15, acc, a);
  }
}

// Morwenna — soft light shaft + ground pool (rear); rising motes (front).
// The halo ring is baked into the sprite — avoid drawing a second one on top.
function fxHoly(c, r, tt, cy, tilt, col, acc, front) {
  if (!front) {
    c.save();
    c.scale(1, tilt);
    blob(c, 0, 0, r * 2.8, col, 0.13);
    c.restore();
    blob(c, 0, cy, r * 2.8, col, 0.13);
    const top = cy - r * 4.5;
    const grad = c.createLinearGradient(0, cy, 0, top);
    grad.addColorStop(0, rgbaStr(acc, 0.11));
    grad.addColorStop(1, rgbaStr(acc, 0));
    c.fillStyle = grad;
    c.fillRect(-r * 1.2, top, r * 2.4, cy - top);
    return;
  }
  const n = 5;
  for (let i = 0; i < n; i++) {
    const ph = ((tt * 0.35) + i / n) % 1;
    const ang = i * 2.4 + ph * 3;
    if ((Math.sin(ang) > 0) !== front) continue;
    const x = Math.cos(ang) * r * 2.0;
    const y = cy + r * 0.4 - ph * r * 3.2 + Math.sin(ang) * r * 0.7;
    const a = ph < 0.75 ? 0.32 * ph : 0.32 * (1 - (ph - 0.75) / 0.25);
    blob(c, x, y, r * 0.12, acc, a);
  }
}

// Lys — void motes pulled inward + a dark shimmer ring (dark feathers via particles).
function fxUmbral(c, r, tt, cy, tilt, col, acc, front) {
  const R = r * 3.0;
  if (!front) {
    c.save();
    c.scale(1, tilt);
    blob(c, 0, 0, r * 2.8, col, 0.12);
    c.restore();
    blob(c, 0, cy, r * 2.8, col, 0.13);
    c.save();
    c.translate(0, cy);
    c.scale(1, 0.5);
    c.strokeStyle = rgbaStr(acc, 0.14 + 0.07 * Math.sin(tt * 3));
    c.lineWidth = 1.5;
    c.beginPath();
    c.arc(0, 0, r * 2.2, 0, TAU);
    c.stroke();
    c.restore();
  }
  const n = 8;
  for (let i = 0; i < n; i++) {
    const ph = ((tt * 0.4) + i / n) % 1;
    const ang = i * 2.4 + ph * 5;
    if ((Math.sin(ang) > 0) !== front) continue;
    const rad = R * (1 - ph);
    const x = Math.cos(ang) * rad, y = cy + Math.sin(ang) * rad * 0.5;
    const a = ph < 0.85 ? 0.46 * ph : 0.46 * (1 - (ph - 0.85) / 0.15);
    blob(c, x, y, r * 0.15, acc, a);
  }
}

const STYLE_FN = {
  arcane: fxArcane,
  fire: fxFire,
  quake: fxQuake,
  plague: fxPlague,
  frost: fxFrost,
  radiant: fxRadiant,
  void: fxVoid,
  holy: fxHoly,
  umbral: fxUmbral,
};

Game.prototype.drawBossAmbient = function (c, p, layer) {
  const cfg = BOSS_AMBIENT[p.boss];
  if (!cfg) return;
  const fn = STYLE_FN[cfg.style];
  if (!fn) return;

  const tt = this.nowS;
  const r = p.radius;
  const cy = bossAmbientCenterY(p.boss, r);
  const tilt = this.scale > 0 ? this.scaleY / this.scale : 0.62;
  const front = layer === "front";

  c.save();
  c.globalCompositeOperation = "lighter";
  fn(c, r, tt, cy, tilt, cfg.glow, cfg.accent, front);
  c.restore();

  // Spawn drifting/rising particles once per frame (on the back pass only).
  if (front) return;
  const pf = STYLE_PARTICLES[cfg.style];
  if (pf && Math.random() < pf.chance && this.particles.length < Game.PARTICLE_CAP) {
    const arr = pf.src === "glow" ? cfg.glow : cfg.accent;
    const cstr = `rgb(${arr[0]},${arr[1]},${arr[2]})`;
    const oy = pf.fromTop ? cy - r * 1.2 : cy * 0.5;
    const opts = { kind: "glow" };
    if (pf.grav != null) opts.grav = pf.grav;
    this.particles.push(new Particle(
      p.x + rand(-r * 0.9, r * 0.9),
      p.y + oy + rand(-r * 0.4, r * 0.4),
      rand(-pf.vx, pf.vx),
      rand(pf.vy[0], pf.vy[1]),
      rand(pf.life * 0.8, pf.life),
      cstr,
      rand(pf.size[0], pf.size[1]),
      opts,
    ));
  }
};

Game.prototype.drawTitanFissures = function () {
  const c = this.ctx;
  const t = this.nowS;
  for (const f of this.fissures || []) {
    const front = Math.min(f.front || 0, f.len || 620);
    if (front <= 0) continue;
    const fade = f.front >= (f.len || 620) ? clamp((f.life || 0) / 0.35, 0, 1) : 1;
    if (fade <= 0) continue;

    const ang = f.angle || 0;
    const hw = f.halfW || 46;
    c.save();
    c.translate(f.x, f.y);
    c.rotate(ang);

    c.globalAlpha = 0.08 * fade;
    c.fillStyle = "rgba(255,120,40,0.9)";
    c.fillRect(0, -hw, front, hw * 2);

    const segs = Math.max(3, Math.floor(front / 48));
    const pulse = 0.5 + 0.5 * Math.sin(t * 8 + (f.pulse || 0));
    c.globalCompositeOperation = "lighter";
    c.lineCap = "round";
    c.lineJoin = "round";

    c.globalAlpha = (0.55 + 0.25 * pulse) * fade;
    c.strokeStyle = "rgba(42,32,22,0.95)";
    c.lineWidth = hw * 0.35;
    c.beginPath();
    c.moveTo(0, 0);
    for (let i = 1; i <= segs; i++) {
      const x = front * (i / segs);
      const wobble = Math.sin(i * 3.7 + t * 5 + (f.pulse || 0)) * hw * 0.22;
      c.lineTo(x, wobble);
    }
    c.stroke();

    c.globalAlpha = (0.35 + 0.3 * pulse) * fade;
    c.strokeStyle = "#ffb060";
    c.lineWidth = hw * 0.18;
    c.beginPath();
    c.moveTo(0, 0);
    for (let i = 1; i <= segs; i++) {
      const x = front * (i / segs);
      const wobble = Math.sin(i * 3.7 + t * 5 + (f.pulse || 0)) * hw * 0.22;
      c.lineTo(x, wobble);
    }
    c.stroke();

    const tipY = Math.sin(segs * 3.7 + t * 5 + (f.pulse || 0)) * hw * 0.22;
    c.globalAlpha = (0.7 + 0.3 * pulse) * fade;
    c.fillStyle = "#ff9040";
    c.beginPath();
    c.arc(front, tipY, hw * 0.28, 0, TAU);
    c.fill();

    c.globalCompositeOperation = "source-over";
    c.globalAlpha = 1;
    c.restore();
  }
};
